import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Skeleton,
    alpha,
    CircularProgress,
} from '@mui/material';
import { Assignment, ArrowForward, CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PendingMarksEntryPage } from '../../types/dashboardTypes';
import type { MarksSheetRow } from '../../types/examination';
import { dashboardService } from '../../services/dashboardService';
import {
    AccentChip,
    DashboardCard,
    DashboardEmptyState,
    DashboardSkeletonCard,
    PANEL_SKELETON_SX,
    thinScrollbarSx,
    useDashboardAccent,
    useDashboardTones,
} from './DashboardCard';

interface PendingMarksEntryCardProps {
    // First page of pending-marks, seeded by the main /dashboard/teacher call.
    // The card owns subsequent pages and fetches them on near-bottom scroll.
    initialPage?: PendingMarksEntryPage;
    loading?: boolean;
}

/**
 * Pending Marks Entry Card
 * Displays exams where marks entry is pending for teacher's assigned subjects
 */
const PendingMarksEntryCard: React.FC<PendingMarksEntryCardProps> = memo(({
    initialPage,
    loading = false,
}) => {
    const { isDark, ink } = useDashboardTones();
    const amber = useDashboardAccent('warning');
    const navigate = useNavigate();

    // Local paginated state. Seeded from `initialPage` (page 1, from the
    // parent dashboard fetch). Subsequent pages are fetched here on scroll.
    const [items, setItems] = useState<MarksSheetRow[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // Track in-flight fetches so a fast scroll doesn't double-fire the same page.
    const inFlightPage = useRef<number | null>(null);

    // Reset local state whenever the parent reseeds (e.g. dashboard refetch).
    // We trust the parent's first page as the source of truth on each refetch.
    useEffect(() => {
        if (!initialPage) return;
        setItems(initialPage.items);
        setHasMore(initialPage.hasMore);
        setPage(initialPage.page);
    }, [initialPage]);

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        const nextPage = page + 1;
        if (inFlightPage.current === nextPage) return;
        inFlightPage.current = nextPage;
        setLoadingMore(true);
        try {
            const resp = await dashboardService.getTeacherPendingMarksPage(
                nextPage,
                initialPage?.pageSize
            );
            if (resp.success) {
                setItems(prev => [...prev, ...resp.data.items]);
                setHasMore(resp.data.hasMore);
                setPage(resp.data.page);
            }
        } catch (err) {
            console.error('Failed to load pending-marks page', err);
        } finally {
            setLoadingMore(false);
            inFlightPage.current = null;
        }
    }, [hasMore, loadingMore, page, initialPage?.pageSize]);

    // Infinite scroll: fire loadMore when the scroll container reaches near
    // the bottom (within 60px). A scroll-event listener is enough at this
    // size; if the widget ever needs thousands of items we can switch to
    // IntersectionObserver on a sentinel.
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
            void loadMore();
        }
    }, [loadMore]);

    // Loading skeleton
    if (loading) {
        return (
            <DashboardSkeletonCard sx={PANEL_SKELETON_SX}>
                <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
                {[1, 2, 3].map((i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, p: 1.5 }}>
                        <Box>
                            <Skeleton variant="text" width={150} />
                            <Skeleton variant="text" width={100} />
                        </Box>
                        <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                ))}
            </DashboardSkeletonCard>
        );
    }

    const hasData = items.length > 0;

    // Navigate to marks entry page
    const handleViewAll = () => {
        navigate('/app/examination/marks');
    };

    return (
        <DashboardCard
            accent="warning"
            icon={Assignment}
            title="Pending Marks Entry"
            subtitle="Exams awaiting your marks"
            actions={hasData && (
                <Typography
                    variant="caption"
                    onClick={handleViewAll}
                    sx={{
                        color: amber.main,
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                            textDecoration: 'underline',
                        }
                    }}
                >
                    View All
                    <ArrowForward sx={{ fontSize: 14 }} />
                </Typography>
            )}
        >
            {/* Content Area */}
            <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>

            {/* Pending exams list or Empty State */}
            {hasData ? (
                <Box
                    ref={scrollRef}
                    onScroll={onScroll}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        maxHeight: 280,
                        overflowY: 'auto',
                        ...thinScrollbarSx(ink(0.15)),
                    }}
                >
                    {items.map((item) => (
                        <Box
                            key={item.key}
                            onClick={handleViewAll}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                py: 1.25,
                                px: 1.5,
                                borderRadius: 2,
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    backgroundColor: alpha(amber.main, isDark ? 0.08 : 0.06),
                                },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item.examName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        {item.subjectName}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 3,
                                            height: 3,
                                            borderRadius: '50%',
                                            backgroundColor: 'text.disabled',
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {item.className}
                                    </Typography>
                                    {item.sectionName && (
                                        <>
                                            <Box
                                                sx={{
                                                    width: 3,
                                                    height: 3,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'text.disabled',
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                                {item.sectionName}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                                    <AccentChip
                                        accent={item.status === 'pending' ? 'warning' : 'info'}
                                        label={item.status === 'pending' ? 'Not started' : 'In progress'}
                                        sx={{ height: 18, fontSize: '0.65rem' }}
                                    />
                                    <Typography variant="caption" color="text.disabled">
                                        {item.total - item.marked} of {item.total} to mark
                                    </Typography>
                                </Box>
                            </Box>
                            <ArrowForward
                                sx={{
                                    color: alpha(amber.main, 0.6),
                                    fontSize: 18,
                                    ml: 1
                                }}
                            />
                        </Box>
                    ))}
                    {loadingMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                            <CircularProgress size={18} sx={{ color: amber.main }} />
                        </Box>
                    )}
                </Box>
            ) : (
                <DashboardEmptyState
                    size="roomy"
                    accent="success"
                    icon={CheckCircle}
                    title="All Caught Up!"
                    description="You have no pending marks to enter. Great job staying on top of things!"
                />
            )}
            </Box>
        </DashboardCard>
    );
});

PendingMarksEntryCard.displayName = 'PendingMarksEntryCard';

export default PendingMarksEntryCard;
